"use client";
import isEmpty from "lodash/isEmpty";
import { useEffect, useMemo, useState, FC, ReactNode } from "react";

import {
  DropdownToggle,
  DropdownItem,
  ResponsiveDrawerDropdown,
} from "@stadium/ui/src/components/bootstrap-dropdown";
import Button from "@stadium/checkout/src/components/shared/buttons";
import TextInput from "@stadium/ui/src/components/form/TextInput";
import Label from "@stadium/ui/src/components/form/Label";
import ToolTip from "@stadium/ui/src/components/tooltip/tooltip";

import { useConfig } from "@stadium/hooks/lib/use-config";
import { getAllCountries } from "@stadium/redux-utils/selectors/configSelector";
import { normalizeZip } from "@stadium/helpers/form/normalizer";

import { useCheckoutContext, Country } from "@stadium/checkout/src/contexts";

import { VALID_CPF_TAX_REGEX } from "@stadium/helpers/form/constants";
import { BILLING_DETAILS_STEP_STATE } from "@stadium/checkout/src/constants/order";

import style from "./main.module.scss";

const BILLING_NOTE_MAX_LENGTH = 75;

export const BILLING_COMP_COUNTRY = "country";
export const BILLING_COMP_ZIPCODE = "zipcode";
export const BILLING_COMP_NOTE = "note";
export const BILLING_COMP_TAX = "tax";

type BillingDetailProps = {
  isExistingOrder?: boolean;
  className?: string;
  disabled?: boolean;
  components?: Array<"country" | "zipcode" | "taxNumber" | "note" | "tax">;
  isBillingNoteRequired?: boolean;
  additionalBillingNote?: string;
  renderContinueCta: (props: { isDisabled: boolean; payload: any }) => ReactNode;
  handleEditBillingDetails: (step: string) => void;
};

const defaultComponents = [BILLING_COMP_COUNTRY, BILLING_COMP_ZIPCODE, BILLING_COMP_NOTE];

const BillingDetails: FC<BillingDetailProps> = ({
  isExistingOrder,
  className = style.billingDetailWrapper,
  disabled,
  components = defaultComponents,
  isBillingNoteRequired: isBillingNoteMandatory = false,
  additionalBillingNote = "",
  renderContinueCta = () => {},
  handleEditBillingDetails = () => {},
}) => {
  const isOnlyBillingNoteRequired =
    components.includes(BILLING_COMP_NOTE) && components.length === 1;

  const [isBillingNoteOpen, setIsBillingNoteOpen] = useState(false);
  const [countrySearchValue, setCountrySearchValue] = useState("");
  const [taxError, setTaxError] = useState("");
  const toggleBillingNote = () => setIsBillingNoteOpen((prev) => !prev);

  const includedComponentsSet = useMemo(() => new Set(components), [components]);
  const hasBillingZipCodeComp = includedComponentsSet.has(BILLING_COMP_ZIPCODE);
  const hasBillingCountryComp = includedComponentsSet.has(BILLING_COMP_COUNTRY);
  const hasBillingNoteComp = includedComponentsSet.has(BILLING_COMP_NOTE);
  const hasBillingTaxComp = includedComponentsSet.has(BILLING_COMP_TAX);

  const countries = useConfig(getAllCountries);

  const {
    billingCountry,
    setBillingCountry,
    billingZipCode,
    setBillingZipCode,
    billingNote,
    setBillingNote,
    billingTaxNumber,
    setBillingTaxNumber,
    checkoutStepsState,
    currentCheckoutStep,
    orderData,
  } = useCheckoutContext();

  const {
    billingCode: initBillingNote,
    billingDetail: { countryId: initBillingCountryId, zipcode: initBillingZipCode } = {},
    taxNumber: initBillingTaxNumber,
  } = orderData || {};

  const isBillingDetailAdded = (() => {
    if (isExistingOrder) return true;
    if (isOnlyBillingNoteRequired) return isBillingNoteMandatory ? Boolean(billingNote) : true;
    if (hasBillingTaxComp) {
      return (
        billingTaxNumber &&
        VALID_CPF_TAX_REGEX.test(billingTaxNumber) &&
        (isBillingNoteMandatory ? Boolean(billingNote) : true)
      );
    } else {
      return (
        !isEmpty(billingCountry) &&
        billingZipCode &&
        (!isBillingNoteMandatory || Boolean(billingNote))
      );
    }
  })();

  const isBillingDetailStepSubmitted = checkoutStepsState?.[BILLING_DETAILS_STEP_STATE]?.submitted;
  const isCurrentCheckoutStateBillingDetail =
    currentCheckoutStep === checkoutStepsState?.[BILLING_DETAILS_STEP_STATE]?.key;

  const handleTaxChange = (value) => {
    setBillingTaxNumber(value);

    if (isEmpty(value)) {
      setTaxError("Required");
    } else if (value && !VALID_CPF_TAX_REGEX.test(value)) {
      setTaxError("Invalid Tax Number");
    } else {
      setTaxError("");
    }
  };

  const handleBillingCountryChange = (country) => {
    setBillingCountry(country);
    setBillingZipCode("");
  };

  useEffect(() => {
    if (initBillingCountryId && !isEmpty(countries))
      setBillingCountry(countries.find((country) => country.id === initBillingCountryId));
    if (initBillingNote) {
      setBillingNote(initBillingNote);
      toggleBillingNote();
    }
    if (initBillingZipCode) setBillingZipCode(initBillingZipCode);
    if (initBillingTaxNumber) setBillingTaxNumber(initBillingTaxNumber);
  }, [initBillingCountryId, initBillingNote, initBillingZipCode, initBillingTaxNumber]);

  const continueCtaProps = {
    isDisabled: !isBillingDetailAdded,
    payload: {
      billingDetailAttributes: {
        countryId: billingCountry?.id,
        zipcode: billingZipCode,
      },
      billingCode: billingNote,
      ...(billingTaxNumber && { taxNumber: billingTaxNumber }),
    },
  };

  const handleZipCodeChange = ({ value }) => {
    setBillingZipCode(
      normalizeZip(value, {
        country: billingCountry?.iso,
        isInternational: billingCountry?.international,
      }),
    );
  };

  const renderCountryOption = (country: Country) => {
    return (
      <DropdownItem
        key={country.id}
        eventKey={country.id}
        onSelect={() => handleBillingCountryChange(country)}
        className={style.dropdownItem}
      >
        <div>{country.name}</div>
      </DropdownItem>
    );
  };

  const renderBillingNote = () => {
    if (!isBillingNoteOpen && !isBillingNoteMandatory)
      return (
        <Button
          styleType="link"
          onClick={toggleBillingNote}
          className={style.addBillingBtn}
          data-id="billing-note-toggle"
          disabled={disabled}
        >
          <i className={style.addIcon} /> Add Billing Note{" "}
          <span className={style.optionalText}>(Optional)</span>
        </Button>
      );
    return (
      <div className={style.billNote}>
        <TextInput
          id="billingNote"
          placeholder="Ref #00000"
          value={billingNote}
          onChange={({ value }) => setBillingNote(value)}
          maxLength={BILLING_NOTE_MAX_LENGTH}
          disabled={disabled}
          label={
            <Label showIcon={false} className={style.billLabel}>
              {isBillingNoteMandatory ? (
                "Add Billing Note*"
              ) : (
                <Button
                  styleType="link"
                  onClick={toggleBillingNote}
                  className={style.addBillingBtn}
                  data-id="billing-note-toggle"
                  disabled={disabled}
                >
                  <i className={style.minusIcon} /> Add Billing Note
                </Button>
              )}
              <span className={style.smallLabel}>(For reference on invoice)</span>
            </Label>
          }
        />

        {isBillingNoteMandatory && additionalBillingNote ? (
          <div dangerouslySetInnerHTML={{ __html: additionalBillingNote }} />
        ) : null}

        {isOnlyBillingNoteRequired &&
          renderContinueCta({
            ...continueCtaProps,
            ctaText: isBillingNoteMandatory ? "Continue" : "Save ",
          })}
      </div>
    );
  };

  const renderBillingCountry = () => (
    <div className={style.billCol}>
      <label className={style.billLabel} htmlFor="billingCountry">
        Country*
      </label>
      <ResponsiveDrawerDropdown
        menuClassName={style.countryDropDownMenu}
        id="billingCountry"
        data-id="billingCountry" // used in styling for snackmagic
        unmountOnClose
        heading={<DropdownToggle>Select billing country</DropdownToggle>}
        onToggle={() => setCountrySearchValue("")}
        disabled={disabled}
        label={
          <DropdownToggle
            className={`${style.countryDropdownToggle} ${billingCountry?.name ? style.active : ""}`}
          >
            {billingCountry?.name || "Select Billing Country"}
          </DropdownToggle>
        }
      >
        <div>
          <TextInput
            id="countrySearch"
            placeholder="Search"
            value={countrySearchValue}
            onChange={({ value }) => setCountrySearchValue(value)}
            classes={style.inputWrapper}
            className={style.searchInputField}
            disabled={disabled}
          />
        </div>
        {countries
          .filter(({ name }) => new RegExp(countrySearchValue, "gi").test(name))
          .map(renderCountryOption)}
      </ResponsiveDrawerDropdown>
    </div>
  );

  const renderBillingZipcode = () => (
    <div className={style.billCol}>
      <TextInput
        id="billingZipCode"
        placeholder="Billing zip code / PIN"
        value={billingZipCode}
        onChange={handleZipCodeChange}
        disabled={disabled}
        label={
          <Label showIcon={false} className={style.billLabel}>
            Zip Code / PIN*
          </Label>
        }
      />
    </div>
  );

  const renderBillingTaxNumber = () => (
    <div className={style.taxField}>
      <div className={style.billNote}>
        <TextInput
          id="billingTaxNumber"
          placeholder="Format is 123.456.789-00 or 11.222.333/4444-55"
          onChange={({ value }) => handleTaxChange(value)}
          value={billingTaxNumber}
          className={style.taxInput}
          label={
            <div className={style.labelWrapper}>
              <Label className={style.billLabel} showIcon={false}>
                Tax Number*
              </Label>
              <ToolTip
                containerClass={style.brazilTaxTooltip}
                content="TAX number is mandatory in case of Brazil and will be used as CPF/CNPJ number."
                isLightTheme
                placement="top-start"
                arrow
              >
                <span className={style.iconInfo} />
              </ToolTip>
            </div>
          }
          error={!isEmpty(taxError)}
          suffix={taxError && <div className={style.taxError}>{taxError}</div>}
        />
      </div>
    </div>
  );

  if (isExistingOrder) return null;

  if (isCurrentCheckoutStateBillingDetail)
    return (
      <div className={className}>
        {(hasBillingZipCodeComp || hasBillingCountryComp) && (
          <>
            <div className={`${style.headingBlock} ${style.billingHeading}`}>
              <div className={style.headingContent}>
                <i className={style.billingIcon} />{" "}
                <span aria-label="Billing" tabIndex="0">
                  Billing
                </span>
              </div>
            </div>
            <div className={style.billInputField}>
              {hasBillingCountryComp && renderBillingCountry()}
              {hasBillingZipCodeComp && renderBillingZipcode()}
            </div>
          </>
        )}
        {hasBillingTaxComp && renderBillingTaxNumber()}
        {hasBillingNoteComp && <div className={style.billingNoteBlock}>{renderBillingNote()}</div>}
        {!isOnlyBillingNoteRequired && renderContinueCta(continueCtaProps)}
      </div>
    );

  if (isBillingDetailStepSubmitted)
    return (
      <div className={`${style.billingDetailWrapper} ${style.separatorBlock}`}>
        <div className={`${style.headingBlock} ${style.billingHeading}`}>
          <div className={style.headingContent}>
            <i className={style.billingIcon} />
            <span>Billing</span>
            <Button
              styleType="link"
              onClick={() => handleEditBillingDetails(BILLING_DETAILS_STEP_STATE)}
              className={style.btnEdit}
            >
              Edit
            </Button>
          </div>
        </div>
        {(hasBillingZipCodeComp || hasBillingCountryComp) && (
          <div className={style.billingNoteBlock}>
            <div className={style.smallHeading}>Country, Zip Code</div>
            <p className={style.stateNameHeading}>
              {billingCountry?.name}, {billingZipCode}
            </p>
          </div>
        )}
        {hasBillingNoteComp && (billingNote || isOnlyBillingNoteRequired) && (
          <div className={style.billingNoteBlock}>
            <div className={style.smallHeading}>
              Note <small className={style.textNote}>(For reference on invoice)</small>
              {isBillingNoteMandatory ? null : (
                <span className={style.optionalText}>(Optional)</span>
              )}
            </div>
            <div className={style.largeHeading}>{billingNote || "--"}</div>
          </div>
        )}

        {billingTaxNumber && (
          <div className={style.billingNoteBlock}>
            <div className={style.smallHeading}>Tax Number</div>
            <div className={style.largeHeading}>{billingTaxNumber || "--"}</div>
          </div>
        )}
      </div>
    );

  return (
    <div className={`${style.headingBlock} billing-details`}>
      <div className={style.headingContent}>
        <i className={style.billingIcon} /> Billing
      </div>
    </div>
  );
};

export default BillingDetails;
